import React from 'react';
import { Control, FieldValues, Path, useWatch } from 'react-hook-form';
import { Conditions } from '../types';

interface Props<T> {
	form: string;
	control: Control<T>;
	condition: Conditions<T>;
	children: JSX.Element;
}

const WatchWrapper = <T extends FieldValues>({
	form,
	children,
	condition,
	control,
}: Props<T>) => {
	const [show, setShow] = React.useState(false);
	const conditionalFieldDependencies = Object.keys(condition).map(
		(key) => key as Path<T>,
	);
	const watchField = useWatch({
		control,
		name: conditionalFieldDependencies,
	});

	React.useEffect(() => {
		const shouldShow = conditionalFieldDependencies.every(
			(fieldToTest, index) => {
				// Get the corresponding values this field can be
				const possibleOkValues = condition[fieldToTest];
				// Get the value of the field we're testing
				const currentFieldValue = watchField[index];
				if (
					Array.isArray(possibleOkValues) &&
					!Array.isArray(currentFieldValue)
				) {
					return possibleOkValues.includes(currentFieldValue);
				} else if (
					Array.isArray(possibleOkValues) &&
					Array.isArray(currentFieldValue)
				) {
					return possibleOkValues.some((okValue) =>
						currentFieldValue.includes(okValue),
					);
				} else {
					return false;
				}
			},
		);
		setShow(shouldShow);
	}, [watchField, condition, conditionalFieldDependencies]);

	return show ? children : null;
};

export default WatchWrapper;
